import React, { useState, useEffect } from "react";
import { apiClient } from "@/services/apiClient";
import { CloseIcon, LinkIcon } from "@/components/icons";

/**
 * The `/api/links` endpoint is admin-only. When this component renders inside
 * a portal tree (client/editor/creator magic-link views), hitting it with the
 * admin apiClient produces a 401 that the admin auth handler interprets as a
 * session loss and redirects to `/`. Skip the fetch in those trees.
 */
function isPortalRoute(): boolean {
  if (typeof window === "undefined") return false;
  return /^\/(client|editor|creator)\//.test(window.location.pathname);
}

interface EntityLink {
  id: string;
  sourceType: string;
  sourceId: string;
  targetType: string;
  targetId: string;
  year: number | null;
  createdAt: string | null;
}

interface LinkedResourcesProps {
  entityType: string;
  entityId: string;
  onUnlink?: (linkId: string) => void;
  className?: string;
}

const TYPE_LABELS: Record<string, string> = {
  calendar_event: "Calendar",
  post: "Post",
  idea: "Idea",
  note: "Note",
  collection: "Collection",
};

const TYPE_COLORS: Record<string, string> = {
  calendar_event: "bg-amber-50 text-amber-700 border-amber-200",
  post: "bg-blue-50 text-blue-700 border-blue-200",
  idea: "bg-purple-50 text-purple-700 border-purple-200",
  note: "bg-green-50 text-green-700 border-green-200",
  collection: "bg-cyan-50 text-cyan-700 border-cyan-200",
};

export const LinkedResources: React.FC<LinkedResourcesProps> = ({
  entityType,
  entityId,
  onUnlink,
  className = "",
}) => {
  const [links, setLinks] = useState<EntityLink[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (isPortalRoute()) {
      setLoading(false);
      return;
    }
    let cancelled = false;

    const fetchLinks = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams({
          source_type: entityType,
          source_id: entityId,
        });
        const response = await apiClient.get<{ links: EntityLink[] }>(
          `/api/links?${params.toString()}`,
        );
        if (!cancelled) {
          setLinks(response.links || []);
        }
      } catch {
        // silent
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchLinks();
    return () => {
      cancelled = true;
    };
  }, [entityType, entityId]);

  const handleUnlink = async (linkId: string) => {
    try {
      await apiClient.delete(`/api/links/${linkId}`);
      setLinks((prev) => prev.filter((l) => l.id !== linkId));
      onUnlink?.(linkId);
    } catch {
      // silent
    }
  };

  if (loading || links.length === 0) return null;

  return (
    <div className={`flex flex-wrap items-center gap-1.5 ${className}`}>
      <LinkIcon className="w-3 h-3 text-gray-400 shrink-0" />
      {links.map((link) => {
        const label = TYPE_LABELS[link.targetType] || link.targetType;
        const color = TYPE_COLORS[link.targetType] || "bg-gray-50 text-gray-600 border-gray-200";

        return (
          <span
            key={link.id}
            className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full border ${color}`}
          >
            {label}
            {link.year && (
              <span className="opacity-60">{link.year}</span>
            )}
            {onUnlink && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleUnlink(link.id);
                }}
                className="ml-0.5 p-0.5 rounded-full hover:bg-black/10 transition-colors"
                title="Unlink"
              >
                <CloseIcon className="w-2.5 h-2.5" />
              </button>
            )}
          </span>
        );
      })}
    </div>
  );
};
